import { type RefObject, useEffect } from 'react';

import {
  WX_NAVBAR_RELEASE_CHANNEL,
  WX_NAVBAR_VERSION,
} from '@/config/runtime-env';
import { useAuthContext } from '@/contexts/auth-context';
import { useWxJwtReady } from '@/hooks/use-wx-jwt-ready';
import { domainMeta } from '@/utils/domain-meta';

declare global {
  interface Window {
    WxNavbarComponent?: {
      setConfig: (config: {
        wxServiceBaseUrl?: string;
        stage?: string;
        employerDomain?: string;
        employerBasePath?: string;
      }) => void;
      render: (mountId: string, props: Record<string, unknown>) => void;
      unmount: (mountId: string) => void;
    };
  }
}

type UseWxNavbarOptions = {
  mountRef: RefObject<HTMLDivElement | null>;
  renderProps?: Record<string, unknown>;
};

export default function useWxNavbar({
  mountRef,
  renderProps = {},
}: UseWxNavbarOptions): void {
  const { whoami } = useAuthContext();
  const isJwtReady = useWxJwtReady();

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Don't initialize if no whoami data yet
    if (!whoami) return undefined;

    // Don't initialize until JWT is ready (when required)
    if (!isJwtReady) return undefined;

    const src = `https://ftn-shared-components.fountain.com/wx-navbar/${WX_NAVBAR_VERSION}/release/${WX_NAVBAR_RELEASE_CHANNEL}/wx-navbar.umd.js`;
    const scripts = Array.from(document.getElementsByTagName('script'));
    const alreadyAdded = scripts.some(
      s =>
        s.id === 'wx-navbar-umd' ||
        s.getAttribute('data-wx-navbar-src') === src ||
        s.src === src,
    );

    if (!alreadyAdded && !window.WxNavbarComponent) {
      const script = document.createElement('script');
      script.id = 'wx-navbar-umd';
      script.setAttribute('data-wx-navbar-src', src);
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    }

    const rootEl = mountRef?.current;
    const mountId = rootEl?.id;
    if (!mountId) return undefined;

    function configureAndRender() {
      // Configure the component
      if (window.WxNavbarComponent?.setConfig) {
        try {
          window.WxNavbarComponent.setConfig({
            wxServiceBaseUrl: whoami?.account_wx_api_url,
            stage: domainMeta.environment,
            employerDomain: whoami?.account_wx_domain,
            employerBasePath: '',
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('WxNavbarComponent.setConfig failed', e);
        }
      }

      // Render the component
      if (window.WxNavbarComponent?.render && mountId) {
        try {
          window.WxNavbarComponent.render(mountId, renderProps);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('WxNavbarComponent.render failed', e);
        }
      }
    }

    if (
      window.WxNavbarComponent &&
      typeof window.WxNavbarComponent.render === 'function'
    ) {
      configureAndRender();
    } else {
      const onLoaded = () => configureAndRender();
      window.addEventListener('WxNavbarComponent:loaded', onLoaded);
      const poll = setInterval(() => {
        if (
          window.WxNavbarComponent &&
          typeof window.WxNavbarComponent.render === 'function'
        ) {
          clearInterval(poll);
          configureAndRender();
        }
      }, 200);

      return () => {
        clearInterval(poll);
        window.removeEventListener('WxNavbarComponent:loaded', onLoaded);
      };
    }

    return () => {
      try {
        if (
          window.WxNavbarComponent &&
          typeof window.WxNavbarComponent.unmount === 'function'
        ) {
          window.WxNavbarComponent.unmount(mountId);
        } else if (rootEl) {
          rootEl.innerHTML = '';
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('WxNavbarComponent.unmount failed', e);
      }
    };
  }, [mountRef, renderProps, whoami, isJwtReady]);
}
